// Pure logic for the App Config editor, kept out of remote-config.ts so it is
// unit-testable without a DOM (see remote-config-support.test.ts). Nothing here
// may value-import another project module: node --test cannot resolve the
// project's extension-less imports.

/**
 * The two iPhone Duo kill switches. Both are optional features: the server
 * column defaults to true and a client that is not told otherwise treats them
 * as on. So "missing" always means on, never off.
 */
export interface DuoSwitches {
  duoCameraDirectionEnabled: boolean;
  duoOuterDisplayEnabled: boolean;
}

/** What the form shows for the Duo switches: the row's value, or on when it has none. */
export function duoSwitchValues(source: Partial<DuoSwitches> | null | undefined): DuoSwitches {
  return {
    duoCameraDirectionEnabled: source?.duoCameraDirectionEnabled ?? true,
    duoOuterDisplayEnabled: source?.duoOuterDisplayEnabled ?? true,
  };
}

/**
 * What the form sends for the Duo switches. Both keys are always present: an
 * omitted key would let the server default it to true and quietly turn back on
 * a switch that had been turned off.
 */
export function duoSwitchPayload(checked: Partial<DuoSwitches>): DuoSwitches {
  return duoSwitchValues(checked);
}

/**
 * The iOS 27 one-split-view navigation kill switch. Optional like the Duo
 * switches: the server column defaults to true and an app that is not told
 * otherwise treats it as on, so "missing" always means on, never off.
 */
export interface NavContinuitySwitch {
  navContinuityEnabled: boolean;
}

/** What the form shows for the navigation switch: the row's value, or on when it has none. */
export function navContinuitySwitchValues(
  source: Partial<NavContinuitySwitch> | null | undefined,
): NavContinuitySwitch {
  return { navContinuityEnabled: source?.navContinuityEnabled ?? true };
}

/**
 * What the form sends for the navigation switch. The key is always present: an
 * omitted key would let the server default it to true and quietly turn back on
 * a switch that had been turned off.
 */
export function navContinuitySwitchPayload(checked: Partial<NavContinuitySwitch>): NavContinuitySwitch {
  return navContinuitySwitchValues(checked);
}
